import React, { useRef } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { MapPin, ArrowRight, BadgeCheck, Sparkles } from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { useT } from "@/context/LangContext";

function formatEuro(n) {
  return Number.isInteger(n) ? `${n}` : n.toFixed(1).replace(".", ",");
}

/**
 * Premium 3D partner card with mouse-tracked perspective tilt.
 * Design: image plein cadre, offre en overlay, badges animés,
 * hover reveals a lime accent glow.
 */
export default function Premium3DCard({ partner, onClick, index = 0 }) {
  const { t } = useT();
  const ref = useRef(null);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), { stiffness: 150, damping: 15 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), { stiffness: 150, damping: 15 });

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mouseX.set((e.clientX - r.left) / r.width - 0.5);
    mouseY.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  const unitValue = partner.offer_value || 0;

  return (
    <motion.article
      ref={ref}
      data-testid={`premium-card-${partner.id}`}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.06, ease: [0.2, 0.8, 0.2, 1] }}
      style={{ rotateX, rotateY, transformPerspective: 1200, transformStyle: "preserve-3d" }}
      className="group relative rounded-3xl overflow-hidden cursor-pointer bg-white shadow-md hover:shadow-2xl transition-shadow duration-500 will-change-transform"
    >
      {/* IMAGE with dark overlay */}
      <div className="relative aspect-[4/5] overflow-hidden bg-navy-900" style={{ transform: "translateZ(0)" }}>
        <img
          src={resolveImageUrl(partner.image_url)}
          alt={partner.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
        />
        {/* dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/40 to-transparent" />
        {/* lime edge glow on hover */}
        <div
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ boxShadow: "inset 0 0 60px 0 rgba(191,255,0,0.25)" }}
        />

        {/* TOP badges */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-1.5" style={{ transform: "translateZ(30px)" }}>
          {(partner.badges || []).slice(0, 2).map((b) => (
            <span key={b} className="chip bg-white/95 text-navy-900 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm shadow-sm">
              {b}
            </span>
          ))}
        </div>
        {partner.featured && (
          <span className="absolute top-4 right-4 chip bg-lime-500 text-navy-900 text-[10px] font-black uppercase tracking-wider shadow-lg" style={{ transform: "translateZ(30px)" }}>
            <BadgeCheck className="w-3 h-3" /> {t("card.featured")}
          </span>
        )}
        {partner.spotlight && (
          <span className="absolute top-4 right-4 chip bg-gradient-to-r from-amber-400 to-lime-400 text-navy-900 text-[10px] font-black uppercase tracking-wider shadow-lg" style={{ transform: "translateZ(30px)" }}>
            <Sparkles className="w-3 h-3" /> {t("potm.badge")}
          </span>
        )}

        {/* BOTTOM overlay content */}
        <div className="absolute inset-x-0 bottom-0 p-5 text-white" style={{ transform: "translateZ(40px)" }}>
          <h3 className="font-heading font-black text-xl md:text-2xl leading-tight drop-shadow-md">
            {partner.name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1 text-[11px] text-white/80 uppercase tracking-wider font-semibold">
            <MapPin className="w-3 h-3" />
            <span>{partner.city}</span>
            <span className="opacity-40">·</span>
            <span className="text-lime-400">{partner.category}</span>
          </div>
        </div>

        {/* SAVING chip — floating bottom-right */}
        {unitValue > 0 && (
          <div
            className="absolute bottom-24 right-4 rounded-2xl px-3.5 py-2.5 shadow-xl backdrop-blur-md"
            style={{ background: "rgba(255,255,255,0.95)", transform: "translateZ(60px)" }}
          >
            <div className="text-[9px] uppercase tracking-widest text-navy-900/60 font-bold leading-none">
              {t("card.economy")}
            </div>
            <div className="flex items-baseline gap-0.5 mt-1">
              <span className="font-heading font-black text-2xl leading-none text-navy-900">−{formatEuro(unitValue)}</span>
              <span className="font-heading font-black text-lg text-lime-600">€</span>
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM strip */}
      <div className="p-5 bg-white flex items-center justify-between gap-3">
        <p className="text-sm text-navy-900 font-semibold line-clamp-2 leading-snug flex-1">
          {partner.offer}
        </p>
        <span
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-navy-900 group-hover:bg-lime-400 group-hover:text-navy-900 transition-colors"
          style={{ background: "rgba(11,18,32,0.06)" }}
        >
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </motion.article>
  );
}
